import type {
  CustomWorkCreate,
  Material,
  Order,
  OrderCreate,
  PaymentCreate,
  PaymentSession,
  Quote,
  QuoteCreate,
  QuoteParams,
  SupportingFileRef,
} from "@fprint/shared";
import { ApiError } from "./apiError";

export interface PresignResponse {
  url: string;
  fields: Record<string, string>;
  key: string;
  content_type: string;
  max_bytes: number;
}

export type TokenGetter = () => Promise<string | null>;

async function jsonFetch<T>(
  input: RequestInfo,
  init: RequestInit & { token?: string | null } = {},
): Promise<T> {
  const { token, headers, ...rest } = init;
  const res = await fetch(input, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    // Surface FastAPI's structured `detail` (when the body is JSON) so
    // callers can branch on machine-readable codes (e.g. review_required).
    let detail: unknown;
    try {
      detail = (JSON.parse(body) as { detail?: unknown }).detail;
    } catch {
      detail = undefined;
    }
    throw new ApiError(
      res.status,
      `${res.status} ${res.statusText}: ${body.slice(0, 200)}`,
      detail,
    );
  }
  return (await res.json()) as T;
}

async function authed<T>(
  getToken: TokenGetter | undefined,
  input: RequestInfo,
  init: RequestInit = {},
): Promise<T> {
  const token = getToken ? await getToken() : null;
  return jsonFetch<T>(input, { ...init, token });
}

export async function fetchMaterials(): Promise<Material[]> {
  return jsonFetch<Material[]>("/api/materials");
}

export async function presignUpload(
  filename: string,
  getToken?: TokenGetter,
): Promise<PresignResponse> {
  return authed<PresignResponse>(getToken, "/api/uploads/presign", {
    method: "POST",
    body: JSON.stringify({ filename }),
  });
}

async function uploadToPresigned(
  presign: PresignResponse,
  file: File,
  kind: string,
): Promise<void> {
  // Presigned POST (not PUT): S3 enforces the size cap via the policy's
  // content-length-range. Pre-check locally too for a friendly message instead
  // of an opaque 400 EntityTooLarge.
  if (file.size > presign.max_bytes) {
    const mb = (presign.max_bytes / (1024 * 1024)).toFixed(0);
    throw new Error(`This file is too large. The maximum ${kind} size is ${mb} MB.`);
  }
  const form = new FormData();
  for (const [k, v] of Object.entries(presign.fields)) form.append(k, v);
  form.append("file", file); // the file field MUST be appended last for S3
  const res = await fetch(presign.url, { method: "POST", body: form });
  if (!res.ok) {
    if (res.status === 400 || res.status === 413) {
      const mb = (presign.max_bytes / (1024 * 1024)).toFixed(0);
      throw new Error(`This file is too large. The maximum ${kind} size is ${mb} MB.`);
    }
    throw new Error(`${kind} upload failed: ${res.status} ${res.statusText}`);
  }
}

export async function uploadStl(presign: PresignResponse, file: File): Promise<void> {
  return uploadToPresigned(presign, file, "STL");
}

/** Presign a custom-work supporting file (any type, ≤25 MiB). Auth REQUIRED. */
export async function presignCustomUpload(
  filename: string,
  getToken: TokenGetter,
): Promise<PresignResponse> {
  return authed<PresignResponse>(getToken, "/api/uploads/presign-custom", {
    method: "POST",
    body: JSON.stringify({ filename }),
  });
}

/** Multipart POST of a custom-work supporting file (fields first, file last). */
export async function uploadCustomFile(
  presign: PresignResponse,
  file: File,
): Promise<void> {
  return uploadToPresigned(presign, file, "file");
}

export async function createQuote(body: QuoteCreate, getToken?: TokenGetter): Promise<Quote> {
  return authed<Quote>(getToken, "/api/quotes", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getQuote(id: string, getToken?: TokenGetter): Promise<Quote> {
  return authed<Quote>(getToken, `/api/quotes/${id}`);
}

/** Quotes owned by the signed-in caller. Auth REQUIRED.
 *  Every stage is returned (including declined / failed). */
export async function listQuotes(getToken: TokenGetter): Promise<Quote[]> {
  return authed<Quote[]>(getToken, "/api/quotes");
}

/**
 * Reassign an anon-owned guest quote to the signed-in user. Auth REQUIRED.
 * Idempotent when the caller already owns it; 404 if it belongs to someone else.
 */
export async function claimQuote(id: string, getToken: TokenGetter): Promise<Quote> {
  return authed<Quote>(getToken, `/api/quotes/${id}/claim`, { method: "POST" });
}

/**
 * Submit an `estimated` (or `ready`) quote for operator review. Locks the
 * quantity server-side and moves the quote to in_review / needs_manual_quote.
 * Auth REQUIRED.
 */
export async function submitQuote(
  id: string,
  qty: number,
  getToken: TokenGetter,
): Promise<Quote> {
  return authed<Quote>(getToken, `/api/quotes/${id}/submit`, {
    method: "POST",
    body: JSON.stringify({ qty }),
  });
}

/**
 * Create a custom-work request (always Manual tier, login-first).
 * `files` is capped at 10 by the caller; the cast bridges the generated
 * schema's ≤10-tuple union, which a plain validated array can't satisfy.
 */
export async function createCustomWork(
  body: {
    description: string;
    stl_s3_key?: string | null;
    material_id?: string | null;
    color_id?: string | null;
    files?: SupportingFileRef[];
  },
  getToken: TokenGetter,
): Promise<Quote> {
  const payload: CustomWorkCreate = {
    ...body,
    files: (body.files ?? []) as CustomWorkCreate["files"],
  };
  return authed<Quote>(getToken, "/api/quotes/custom-work", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createPayment(
  body: PaymentCreate,
  getToken: TokenGetter,
): Promise<PaymentSession> {
  return authed<PaymentSession>(getToken, "/api/payments", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// Release this user's payment reservation for a quote when the customer
// abandons/declines the payment popup, so the quote isn't held for the whole
// reservation TTL. Best-effort — a failed release just leaves it to expire.
export async function releasePayment(quoteId: string, getToken: TokenGetter): Promise<void> {
  try {
    await authed<void>(getToken, `/api/payments/${quoteId}/release`, { method: "POST" });
  } catch {
    // non-fatal: the reservation expires on its own
  }
}

export async function createOrder(body: OrderCreate, getToken: TokenGetter): Promise<Order> {
  return authed<Order>(getToken, "/api/orders", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function listOrders(getToken: TokenGetter): Promise<Order[]> {
  return authed<Order[]>(getToken, "/api/orders");
}

export type {
  CustomWorkCreate,
  Material,
  Order,
  OrderCreate,
  PaymentCreate,
  PaymentSession,
  Quote,
  QuoteCreate,
  QuoteParams,
  SupportingFileRef,
};
